<template>
  <div>
    <h2 class="text-left alert" :class="[removed ? 'alert-danger' : 'alert-success']">
      Preventive Animal Health and Vaccination
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[removed ? 'glyphicon-plus' : 'glyphicon-remove']"></span>
      </a>
    </h2>
    <template v-if="!removed">
      <report-comment
        field_name="heading_preventive_animal_health_and_vaccination"
        :model="model.COMMENTS"
        :heading="true"
      ></report-comment>

      <subsection title="BVD Testing Results" :step-name="stepName" subsection="bvd_testing_results_vetsouth">
        <report-comment
          field_name="heading_bvd_testing_results_vetsouth"
          :model="model.COMMENTS"
          :heading="true"
        ></report-comment>

        <div v-if="model.CHARTS.bvd_results.length > 0">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Season</th>
                <th>Supply</th>
                <th>Vet</th>
                <th>Test Type</th>
                <th>Description</th>
                <th>Animals Tested</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(bvd, index) in model.CHARTS.bvd_results" :key="`bvd_results-${index}`">
                <td class="text-left">{{ bvd[0] }}</td>
                <td class="text-left">{{ bvd[1] }}</td>
                <td class="text-left">{{ bvd[2] }}</td>
                <td class="text-left">{{ bvd[3] }}</td>
                <td class="text-left">{{ bvd[4] }}</td>
                <td class="text-left">{{ bvd[5] }}</td>
                <td class="text-left">{{ bvd[6] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <p class="text-left">No Results</p>
        </div>
      </subsection>

      <subsection
        title="BVD Testing Results (spreadsheet data)"
        :step-name="stepName"
        subsection="bvd_spreadsheet_testing_results"
      >
        <div v-if="model.CHARTS.bvd_spreadsheet_results.length > 0">
          <small class="text-muted">
            Some results shown here may repeat those shown above but are included here for completeness
          </small>
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Season</th>
                <th>Source</th>
                <th>Herd</th>
                <th>Visit</th>
                <th>AbELISA</th>
                <th>SP</th>
                <th>PCR</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(bvd, index) in model.CHARTS.bvd_spreadsheet_results"
                :key="`bvd_spreadsheet_results-${index}`"
              >
                <td class="text-left">{{ bvd['Season'] }}</td>
                <td class="text-left">{{ bvd['Source'] }}</td>
                <td class="text-left">{{ bvd['Herd'] }}</td>
                <td class="text-left">{{ bvd['Visit'] }}</td>
                <td class="text-left">{{ bvd['AbELISA'] }}</td>
                <td class="text-left">{{ bvd['SP'] }}</td>
                <td class="text-left">{{ bvd['PCR'] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <p class="text-left">No Results</p>
        </div>
      </subsection>

      <subsection title="BVD Monitoring Requirements" :step-name="stepName" subsection="bvd_monitoring">
        <div v-for="farm in model.FARMS" :key="`bvd_monitoring-${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>

          <dl class="dl-horizontal">
            <dt>Bulk Milk LIC</dt>
            <dd>{{ model[`farm_${farm.id}-wants_bvd_mon_lic`] || '-' }}</dd>

            <dt>LIC Bulk Milk Package?</dt>
            <dd>{{ model[`farm_${farm.id}-wants_bvd_mon_gribbles`] || '-' }}</dd>

            <dt>Other</dt>
            <dd>{{ model[`farm_${farm.id}-wants_bvd_mon_other`] || '-' }}</dd>

            <dt>Vaccination</dt>
            <dd>{{ model[`farm_${farm.id}-wants_bvd_mon_vax`] || '-' }}</dd>
          </dl>
        </div>
        <div>
          <h4 class="text-left">R2s</h4>

          <dl class="dl-horizontal">
            <dt>Individual Ag</dt>
            <dd>{{ model['wants_bvd_mon_r2_ind_ag'] || '-' }}</dd>

            <dt>Pre Mating Ab</dt>
            <dd>{{ model['wants_bvd_mon_r2_premate_ab'] || '-' }}</dd>

            <dt>Vaccination</dt>
            <dd>{{ model['wants_bvd_mon_r2_vax'] || '-' }}</dd>
          </dl>
        </div>
        <div>
          <h4 class="text-left">Calves</h4>

          <dl class="dl-horizontal">
            <dt>Individual Ag</dt>
            <dd>{{ model['wants_bvd_mon_r1_ind_ag'] || '-' }}</dd>

            <dt>Post Winter Ab</dt>
            <dd>{{ model['wants_bvd_mon_r1_winter_ab'] || '-' }}</dd>

            <dt>Vaccination</dt>
            <dd>{{ model['wants_bvd_mon_r1_vax'] || '-' }}</dd>
          </dl>
        </div>
        <div>
          <h4 class="text-left">Bulls</h4>

          <dl class="dl-horizontal">
            <dt>Individual Ag</dt>
            <dd>{{ model['wants_bvd_mon_bull_ind_ag'] || '-' }}</dd>

            <dt>Post Winter Ab</dt>
            <dd>{{ model['wants_bvd_mon_bull_winter_ab'] || '-' }}</dd>

            <dt>Vaccination</dt>
            <dd>{{ model['wants_bvd_mon_bull_vax'] || '-' }}</dd>
          </dl>
        </div>
      </subsection>

      <subsection title="Johnes Disease" :step-name="stepName" subsection="johnes_disease">
        <div v-for="farm in model.FARMS" :key="`johnes_disease_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment :field_name="`farm_${farm.id}-other_issues_johnes`" :model="model.COMMENTS"></report-comment>

          <dl class="dl-horizontal">
            <dt>Do you want to Johnes test?</dt>
            <dd>{{ model[`farm_${farm.id}-wants_herd_test`] || '-' }}</dd>
          </dl>
        </div>
      </subsection>

      <subsection title="Lepto Products Purchased" :step-name="stepName" subsection="lepto_products_purchased">
        <div v-if="model.CHARTS.lepto_purchases.length > 0">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Purchase Date</th>
                <th>Animal</th>
                <th>Product</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(lp, index) in model.CHARTS.lepto_purchases" :key="index">
                <td class="text-left">{{ lp[0] }}</td>
                <td class="text-left">{{ lp[1] }}</td>
                <td class="text-left">{{ lp[2] }}</td>
                <td class="text-left">{{ lp[3] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <p class="text-left">No Results</p>
        </div>
      </subsection>

      <subsection title="Lepto Vaccination" :step-name="stepName" subsection="lepto_vaccination">
        <div>
          <report-comment field_name="heading_lepto_vaccination" :model="model.COMMENTS"></report-comment>

          <div class="row">
            <div class="col-md-3"><strong>Calves</strong></div>
            <div class="col-md-3"><strong>Month</strong></div>
            <div class="col-md-3"><strong>Vax Type</strong></div>
            <div class="col-md-3"><strong>Est. number of animals</strong></div>
          </div>
          <div class="row">
            <div class="col-md-3">1st Shot</div>
            <div class="col-md-3">
              {{ model.first_shot_month || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.first_shot_vax_type || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.first_shot_est_number_animals || '-' }}
            </div>
          </div>
          <div class="row">
            <div class="col-md-3">2nd Shot</div>
            <div class="col-md-3">
              {{ model.second_shot_month || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.second_shot_vax_type || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.second_shot_est_number_animals || '-' }}
            </div>
          </div>
          <div class="row">
            <div class="col-md-3">3rd Shot</div>
            <div class="col-md-3">
              {{ model.third_shot_month || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.third_shot_vax_type || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.third_shot_est_number_animals || '-' }}
            </div>
          </div>
          <br />
          <h4>Herds and Heifers</h4>
          <div class="row">
            <div class="col-md-3"><strong>Animal</strong></div>
            <div class="col-md-3"><strong>Month</strong></div>
            <div class="col-md-3"><strong>Vax Type</strong></div>
            <div class="col-md-3"><strong>Est. number of animals</strong></div>
          </div>
          <div class="row" v-for="farm in model.FARMS" :key="`aniamlrow-${farm.id}`">
            <div class="col-md-3">
              {{ farm.farm_label }}
            </div>
            <div class="col-md-3">
              {{ model[`animallmdata_${farm.id}-month`] || '-' }}
            </div>
            <div class="col-md-3">
              {{ model[`animallmdata_${farm.id}-vax_type`] || '-' }}
            </div>
            <div class="col-md-3">
              {{ model[`animallmdata_${farm.id}-est_number_animals`] || '-' }}
            </div>
          </div>
          <div class="row">
            <div class="col-md-3">Heifers</div>
            <div class="col-md-3">
              {{ model.heifers_month || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.heifers_vax_type || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.heifers_est_number_animals || '-' }}
            </div>
          </div>

          <div class="row">
            <div class="col-md-3">Other (Beef, Bull, etc)</div>
            <div class="col-md-3">
              {{ model.other_animal_month || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.other_animal_vax_type || '-' }}
            </div>
            <div class="col-md-3">
              {{ model.other_animal_est_number_animals || '-' }}
            </div>
          </div>
          <hr />
          <div class="row">
            <div class="col-md-4">Programme Type: {{ model.lepto_vax_program || '-' }}</div>
            <div class="col-md-4">Vaccination Completed in: {{ model.lepto_vax_completed_in || '-' }}</div>
            <div class="col-md-4">Numbers of Pigs on Farm: {{ model.pigs_on_farm || '-' }}</div>
          </div>
        </div>
      </subsection>
    </template>
  </div>
</template>

<script>
import mixin from './mixin'

export default {
  mixins: [mixin],
  name: 'PreventiveHealthAndVaccination',
}
</script>
